import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  Typography,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  TextField,
  Button,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  fetchMedicalReportsByClientId,
  nextAppointmentNotes,
  createAppointmentNote,
  fetchClientNotes,
  futureAppointments,
  fetchAppointmentMetadata,
  fetchGroupedFilledServiceTemplatesByClientId,
  createEditedFilledServiceTemplate,
  loadClientDocumentsFileCollections,
  loadClientHistoryData,
} from "../../../services/api";
import { useRoles } from "../../../context/AuthenticationTokenContext";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faKeyboard,
  faChalkboardTeacher,
  faNoteSticky,
  faPen,
  faCalendarCheck,
  faCalendarPlus,
  faCalendarXmark,
  faClock,
  faHistory,
  faFile,
} from "@fortawesome/free-solid-svg-icons";

import PersonAvatar from "../PersonAvatar";
import AppointmentTimelineItem from "./appointmentHistory/AppointmentTimelineItem.jsx";
import * as f from "../../../utils/formatter.js";
import FilterMenu from "./appointmentHistory/FilterMenu.jsx";
import { useSnackbarMessage } from "../../Snackbar.jsx";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import ExportMenu from "./appointmentHistory/ExportMenu.jsx";
import { parseUtcDateTime } from "shared/src/date.mjs";
import HistoryRecord from "./appointmentHistory/HistoryRecord";
import MedicalReport from "./appointmentHistory/MedicalReport";
import Webhook, { getWebhookTitle } from "./appointmentHistory/Webhook";
import { permissions } from "shared/src/permissions.mjs";
import { AppointmentOppositeContent } from "./appointmentHistory/Appointment";
import ReceptionNote from "./appointmentHistory/ReceptionNote";

export const FILTER_TYPES = {
  APPOINTMENT: "APPOINTMENT",
  MEDICAL_REPORT: "MEDICAL_REPORT",
  NEXT_APPOINTMENT_NOTE: "NEXT_APPOINTMENT_NOTE",
  CLIENT_NOTE: "CLIENT_NOTE",
  RECEPTION_NOTE: "RECEPTION_NOTE",
  FLOOR_MANAGER_NOTE: "FLOOR_MANAGER_NOTE",
  APPOINTMENT_EVENTS: "APPOINTMENT_EVENTS",
  FILE_EVENT: "FILE_EVENT",
  HISTORY_RECORD: "HISTORY_RECORD",
  WEBHOOK: "WEBHOOK",
};

export const DEFAULT_SELECTED_FILTERS = [
  FILTER_TYPES.APPOINTMENT,
  FILTER_TYPES.MEDICAL_REPORT,
  FILTER_TYPES.NEXT_APPOINTMENT_NOTE,
  FILTER_TYPES.CLIENT_NOTE,
  FILTER_TYPES.RECEPTION_NOTE,
  FILTER_TYPES.FLOOR_MANAGER_NOTE,
  FILTER_TYPES.FILE_EVENT,
  FILTER_TYPES.HISTORY_RECORD,
  FILTER_TYPES.WEBHOOK,
];

function getAppointmentEventIcon(eventType) {
  switch (eventType) {
    case "CREATED":
      return faCalendarPlus;
    case "CANCELLED":
      return faCalendarXmark;
    case "RESCHEDULED":
      return faCalendarCheck;
    case "TIME_CHANGED":
      return faClock;
    default:
      return faCalendarPlus;
  }
}

async function fetchTimelineData(clientId) {
  const [
    groupedFilledServiceTemplates = [],
    medicalReportGroups = [],
    notes = [],
    clientNotes = [],
    appointmentMetadata = [],
    docsRes = {},
    clientHistory,
  ] = await Promise.all([
    fetchGroupedFilledServiceTemplatesByClientId(clientId).catch(() => []),
    fetchMedicalReportsByClientId(clientId).catch(() => []),
    nextAppointmentNotes({ client_id: { eq: clientId } })
      .then((res) => res?.data?.nextAppointmentNotes?.data || [])
      .catch(() => []),
    fetchClientNotes(clientId)
      .then((res) => res?.data?.clientNotes || [])
      .catch(() => []),
    fetchAppointmentMetadata(clientId)
      .then((res) => res?.data?.appointmentMetadata || [])
      .catch(() => []),
    loadClientDocumentsFileCollections({ client_id: clientId }).catch(
      () => ({}),
    ),
    loadClientHistoryData({
      client_id: clientId,
      webhooksFilter: {
        payload: { name: "client_id", expr: { eq: clientId } },
      },
    }),
  ]);

  return {
    groupedFilledServiceTemplates,
    medicalReportGroups,
    notes,
    clientNotes,
    appointmentMetadata,
    docsRes,
    clientHistory,
  };
}

function getEntryDisplayTimeValue(entry) {
  switch (entry.type) {
    case FILTER_TYPES.APPOINTMENT:
      return (
        entry.original_created_at || entry.appointment_start || entry.created_at
      );

    case FILTER_TYPES.MEDICAL_REPORT:
      return entry.original_created_at || entry.created_at;
    case FILTER_TYPES.APPOINTMENT_EVENTS:
      return entry.timestamp || entry.created_at || entry.appointment_start;

    case FILTER_TYPES.NEXT_APPOINTMENT_NOTE:
    case FILTER_TYPES.RECEPTION_NOTE:
    case FILTER_TYPES.CLIENT_NOTE:
    case FILTER_TYPES.FLOOR_MANAGER_NOTE:
    default:
      return entry.created_at;
  }
}

function getEntryDisplayTime(entry) {
  return parseUtcDateTime(getEntryDisplayTimeValue(entry));
}

async function transformTimeLineData(data, t) {
  const {
    groupedFilledServiceTemplates,
    medicalReportGroups,
    notes,
    clientNotes,
    appointmentMetadata,
    docsRes,
    clientHistory,
  } = data;

  const getUserNameFromNoteType = (noteType) => {
    const typeLabels = {
      nurse: t("roles.nurse") || "Nurse",
      receptionist: t("roles.receptionist") || "Receptionist",
      back_office: t("roles.backOffice") || "Back Office",
      floor_manager: t("roles.floorManager") || "Floor Manager",
      client_note: t("client.noteCreator") || "Note Creator",
    };
    return typeLabels[noteType] || t("client.unknownEmployee");
  };

  const getRoleDisplayName = (role) => {
    if (!role) return null;

    return t(`roles.${role}`);
  };

  const documents = docsRes?.data?.client?.fileCollections || [];
  const fileEvents = documents.flatMap((c) =>
    (c.files || []).map((f) => ({
      type: FILTER_TYPES.FILE_EVENT,
      id: `file-${c.id}-${f.id}`,
      created_at: c.created_at,
      collection_name: c.class_name,
      state_name: c.state_name,
      file_name: f.name,
    })),
  );

  const initialSelectedVersions = {};
  medicalReportGroups.forEach((group) => {
    const latestVersion = group.versions.find((v) => v.is_latest);
    if (latestVersion) {
      initialSelectedVersions[group.report_group_id] = latestVersion.version_id;
    }
  });

  clientNotes.forEach((note) => {
    if (
      note.note_type === "receptionist" &&
      note.receptionist_versions?.length
    ) {
      const latest =
        note.receptionist_versions.find((v) => v.is_latest) ||
        note.receptionist_versions[note.receptionist_versions.length - 1];
      if (latest) {
        initialSelectedVersions[`client-note-${note.id}`] = latest.version_id;
      }
    }
  });

  groupedFilledServiceTemplates.forEach((group) => {
    const latest =
      group.versions.find((v) => v.is_latest) ||
      group.versions[group.versions.length - 1];
    if (latest) {
      initialSelectedVersions[group.group_id] = latest.version_id;
    }
  });

  const formattedEntries = [
    ...groupedFilledServiceTemplates.map((group) => {
      const latest =
        group.versions.find((v) => v.is_latest) ||
        group.versions[group.versions.length - 1];

      const original =
        group.versions.find((v) => v.is_original) || group.versions[0];

      const originalCreatedAt =
        original?.created_at ||
        group.appointment_start ||
        latest?.created_at ||
        latest?.edited_at ||
        null;

      const updatedAt =
        latest?.edited_at || latest?.created_at || originalCreatedAt;

      return {
        type: FILTER_TYPES.APPOINTMENT,
        id: group.group_id,
        group_id: group.group_id,
        appointment_id: group.appointment_id,
        service_name: group.service_name || t("client.unknownService"),
        appointment_start: group.appointment_start,
        employee_name: group.employee_name || t("client.unknownEmployee"),
        employee_role: original?.filled_data?.roles
          ?.map((r) => getRoleDisplayName(r))
          ?.join(", "),
        versions: group.versions,
        original_created_at: originalCreatedAt,
        updated_at: updatedAt,
      };
    }),

    ...medicalReportGroups.map((group) => ({
      type: FILTER_TYPES.MEDICAL_REPORT,
      id: group.report_group_id,
      report_group_id: group.report_group_id,
      report_type: group.report_type,
      original_created_at: group.original_created_at,
      employee_name: group.employee_name || t("client.unknownEmployee"),
      employee_role: getRoleDisplayName(group.employee_role),
      versions: group.versions,
    })),

    ...notes.map((note, index) => ({
      type: FILTER_TYPES.NEXT_APPOINTMENT_NOTE,
      id: note.id ? `next-note-${note.id}` : `next-note-${index}`,
      note: note.note ?? "",
      created_at: parseUtcDateTime(note.created_at),
      employee_role: getRoleDisplayName(note.role),
      employee_name:
        note.employee?.first_name || note.employee?.last_name
          ? `${note.employee?.first_name ?? ""} ${note.employee?.last_name ?? ""}`.trim()
          : note.username || null,
    })),

    ...clientNotes
      .map((note) => {
        if (
          note.note_type === "receptionist" &&
          (note.note_content == null || note.note_content.length === 0) &&
          note.service_id == null
        ) {
          return;
        }

        const originalReceptionVersion =
          (note.receptionist_versions || []).find((v) => v.is_original) ||
          (note.receptionist_versions || [])[0];

        const noteCreatedAtStable =
          originalReceptionVersion?.created_at ||
          note.created_at ||
          note.createdAt ||
          null;

        return {
          type:
            note.note_type === "receptionist"
              ? FILTER_TYPES.RECEPTION_NOTE
              : note.note_type === "floor_manager"
                ? FILTER_TYPES.FLOOR_MANAGER_NOTE
                : FILTER_TYPES.CLIENT_NOTE,

          id: `client-note-${note.id}`,
          note: note.note_content,
          note_type: note.note_type,
          created_at: noteCreatedAtStable,

          employee_name:
            note.created_by_employee_name ||
            note.created_by_name ||
            getUserNameFromNoteType(note.note_type),

          employee_role: note.created_by_role
            ? getRoleDisplayName(note.created_by_role)
            : getUserNameFromNoteType(note.note_type),

          doctor_required: note.doctor_required,
          service_id: note.service_id,
          reservation_count: note.reservation_count,
          reservation_unit: note.reservation_unit,

          versions: note.receptionist_versions || [],
        };
      })
      .filter((n) => n != null),

    ...appointmentMetadata.map((metadata) => ({
      type: FILTER_TYPES.APPOINTMENT_EVENTS,
      id: metadata.id,
      event_type: metadata.event_type,
      timestamp: metadata.timestamp,
      appointment_id: metadata.appointment_id,
      status_id: metadata.status_id,
      rescheduled_from_id: metadata.rescheduled_from_id,
      cancellation_reason: metadata.cancellation_reason,
      cancellation_type: metadata.cancellation_type,
      appointment_start: metadata.appointment_start,
      user_name: metadata.user_name,
      employee_name: metadata.employee_name,
      employee_role: getRoleDisplayName(metadata.employee_role),
      raw_data: metadata.raw_data,
      created_at: metadata.timestamp,
      service_name: metadata.service_name,
      original_service_name: metadata.original_service_name,
      original_appointment_date: metadata.original_appointment_date,
    })),
    ...fileEvents,

    ...(clientHistory?.data?.client?.historyRecords?.map((hr) => ({
      ...hr,
      type: FILTER_TYPES.HISTORY_RECORD,
    })) ?? []),

    ...(clientHistory?.data?.webhooks?.data?.map((w) => ({
      ...w,
      type: FILTER_TYPES.WEBHOOK,
      employee_name: f.fullName(w.employee),
      employee_role: w.payload.roles
        ?.map((r) => getRoleDisplayName(r))
        ?.join(", "),
    })) ?? []),
  ];

  formattedEntries.sort(
    (a, b) => getEntryDisplayTime(b) - getEntryDisplayTime(a),
  );

  return {
    timeLineEntries: formattedEntries,
    selectedVersions: initialSelectedVersions,
  };
}

const timelineEntryContentComponent = {
  [FILTER_TYPES.HISTORY_RECORD]: HistoryRecord,
  [FILTER_TYPES.MEDICAL_REPORT]: MedicalReport,
  [FILTER_TYPES.WEBHOOK]: Webhook,
  [FILTER_TYPES.RECEPTION_NOTE]: ReceptionNote,
};

function TimelineEntryContent(props) {
  const { entry } = props;
  const Component = timelineEntryContentComponent[entry.type];
  if (Component == null) {
    return null;
  }

  return <Component {...props} />;
}

const timelineEntryOppositeContentComponent = {
  [FILTER_TYPES.APPOINTMENT]: AppointmentOppositeContent,
};

function TimelineEntryOppositeContent(props) {
  const { entry } = props;
  const Component = timelineEntryOppositeContentComponent[entry.type];
  if (Component == null) {
    return null;
  }

  return <Component {...props} />;
}

const timelineEntryTitle = {
  [FILTER_TYPES.APPOINTMENT]: ({ entry }) => entry.service_name,
  [FILTER_TYPES.APPOINTMENT_EVENTS]: ({ entry }) => entry.service_name,
  [FILTER_TYPES.NEXT_APPOINTMENT_NOTE]: ({ t }) =>
    t("client.nextAppointmentNote"),
  [FILTER_TYPES.RECEPTION_NOTE]: ({ t }) =>
    t("client.receptionNote", "Zápis pro recepci"),
  [FILTER_TYPES.FLOOR_MANAGER_NOTE]: ({ t }) =>
    t("client.floorManagerNote", "Poznámka pro FM"),
  [FILTER_TYPES.CLIENT_NOTE]: ({ t }) => t("client.note", "Poznámka"),
  [FILTER_TYPES.FILE_EVENT]: ({ t, entry }) =>
    entry.collection_name || t("Dokument"),
  [FILTER_TYPES.HISTORY_RECORD]: ({ t }) => t("client.historyRecord"),
  [FILTER_TYPES.MEDICAL_REPORT]: ({ t }) => t("client.medicalReportLegacy"),
  [FILTER_TYPES.WEBHOOK]: getWebhookTitle,
};

function TimelineEntryTitle(props) {
  const { entry } = props;
  const titleF = timelineEntryTitle[entry.type];

  return titleF(props);
}

function ReceptionNoteContent({ entry }) {
  const { t } = useTranslation();
  const isReceptionNote = entry.note_type === "receptionist";

  return (
    <Box>
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 2 }}>
        <strong>{t("client.noNoteLabel", "Poznámka:")}</strong>{" "}
        {entry.note || <>{t("client.noNoteValue", "---")}</>}
      </Typography>

      {isReceptionNote && (
        <Box>
          {entry.service && (
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>{t("client.service", "Služba")}:</strong>{" "}
              {entry.service.name}
            </Typography>
          )}

          {(() => {
            const noTerm =
              entry.reservation_count == null ||
              entry.reservation_unit == null ||
              Number(entry.reservation_count) === 0;

            return noTerm ? (
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Rezervace:</strong> Klient nepotřebuje termín
              </Typography>
            ) : (
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>Rezervace:</strong> {entry.reservation_count}{" "}
                {entry.reservation_unit}
              </Typography>
            );
          })()}

          {entry.doctor_required != null && (
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              <strong>
                {t("client.doctorRequired", "Je potřeba doktor")}:
              </strong>{" "}
              {entry.doctor_required
                ? t("client.yes", "Ano")
                : t("client.no", "Ne")}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

function AppointmentEventContent({ entry }) {
  const { t } = useTranslation();
  const when = entry.appointment_start
    ? f.shortDateTime(entry.appointment_start)
    : "";
  const roles = useRoles();
  const canSeeCancellationReasonAndType = isGrantedAny(
    roles,
    new Set([
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  return (
    <Box>
      <Typography variant="body1" component="div">
        <strong>{t(`appointment.eventType.${entry.event_type}`)}: </strong>

        {entry.event_type === "CREATED" && (
          <>
            {entry.service_name}
            {when ? `, ${when}` : ""}
          </>
        )}

        {entry.event_type === "CANCELLED" && (
          <>
            {t("appointmentEvents.cancelledLine", "{{service}}, {{when}}", {
              service:
                entry.service_name ||
                t("client.unknownService", "Neznámá služba"),
              when: when || t("common.unknownDate", "Neznámé datum"),
            })}

            {canSeeCancellationReasonAndType && (
              <>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  <strong>
                    {t("appointmentEvents.cancelBy", "Zrušil(a)")}:{" "}
                  </strong>
                  {entry.cancellation_type === "client"
                    ? t("appointmentEvents.byClient", "Klient")
                    : entry.cancellation_type === "clinic"
                      ? t("appointmentEvents.byClinic", "Klinika")
                      : t("appointmentEvents.byUnknown", "Neznámé")}
                </Typography>

                {entry.cancellation_reason && (
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    <strong>
                      {t(
                        "appointmentEvents.cancellationReason",
                        "Důvod zrušení",
                      )}
                      :
                    </strong>{" "}
                    {entry.cancellation_reason}
                  </Typography>
                )}
              </>
            )}
          </>
        )}

        {entry.event_type === "RESCHEDULED" &&
          entry.original_service_name &&
          entry.original_appointment_date && (
            <>
              <br />
              {`Schůzka ${entry.original_service_name}, ${f.shortDateTime(
                entry.original_appointment_date,
              )}, byla nahrazena schůzkou ${entry.service_name}, ${f.shortDateTime(
                entry.appointment_start,
              )}`}
            </>
          )}

        {entry.event_type === "TIME_CHANGED" && (
          <>
            {entry.service_name
              ? t(
                  "appointmentEvents.serviceTimeWasChanged",
                  "Změněn čas termínu: {{serviceName}}",
                  { serviceName: entry.service_name },
                )
              : t("appointmentEvents.timeWasChanged", "Čas termínu byl změněn")}
          </>
        )}
      </Typography>
    </Box>
  );
}

const ClientActivityLogTimeline = ({ clientId }) => {
  const theme = useTheme();
  const { t } = useTranslation(["translation", "serviceTemplates"]);
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_SELECTED_FILTERS);
  const [noteForHistoryAxisDialogOpen, setNoteForHistoryAxisDialogOpen] =
    useState(false);
  const [noteInput, setNoteInput] = useState("");
  const [selectedVersions, setSelectedVersions] = useState({});
  const [saving, setSaving] = useState(false);

  const displaySnackbarMessage = useSnackbarMessage();

  const roles = useRoles();
  const canSetNoteForHistoryAxis = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Doctor,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
    ]),
  );

  const canEdit = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Manager_Customer_Care,
      role.Manager_Clinic,
    ]),
  );

  const canSeeEditedBy = isGrantedAny(
    roles,
    new Set([
      role.Manager_Customer_Care,
      role.Manager_Clinic,
      role.Read_Only_Full,
    ]),
  );

  const canSeeAppointmentEvents = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only,
      role.Read_Only_Full,
    ]),
  );

  const canSeeNoteForFloorManager = isGrantedAny(
    roles,
    new Set([
      role.Floor_Manager,
      role.Treatment_Coordinator,
      role.Back_Office,
      role.Front_Office,
      role.Dental_Assistant,
      role.Manager_Clinic,
      role.Manager_Customer_Care,
      role.Read_Only_Full,
    ]),
  );

  const handleSaveNote = async () => {
    if (!noteInput.trim()) return;

    setSaving(true);

    try {
      const appointmentResponse = await futureAppointments({
        filter: {
          client_id: { eq: clientId },
          start: { gte: "2000-01-01T00:00:00Z" },
        },
      });

      const appointments = appointmentResponse?.data?.appointments?.data || [];

      if (appointments.length === 0) {
        alert("No appointments found for this client. Cannot create note.");
        return;
      }

      const latestAppointment = appointments[0];

      const noteType = "client_note";

      await createAppointmentNote({
        appointment_id: latestAppointment.id,
        note_type: noteType,
        note_content: noteInput.trim(),
        doctor_required: false,
      });

      setNoteInput("");
      setNoteForHistoryAxisDialogOpen(false);

      setTimeout(() => {
        fetchTimeline();
      }, 500);

      displaySnackbarMessage({
        title: "Poznámka byla úspěšně uložena",
        content: "",
      });
    } catch (error) {
      console.error("Failed to save note:", error);

      displaySnackbarMessage({
        title: "Poznámku se nepodařilo uložit. Prosím, zkuste to znovu.",
        severity: "error",
        content: "",
      });
    } finally {
      setSaving(false);
    }
  };

  const fetchTimeline = useCallback(async () => {
    if (!clientId) {
      setError(t("client.noActivityLogs"));
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await fetchTimelineData(clientId);
      const { timeLineEntries, selectedVersions } = await transformTimeLineData(
        data,
        t,
      );
      setSelectedVersions(selectedVersions);
      setTimelineEntries(timeLineEntries);
      setError(null);
    } catch (error) {
      console.error("Error fetching timeline:", error);
      setError(t("client.errorLoadingData"));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const handleVersionChange = (reportGroupId, versionId) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [reportGroupId]: versionId,
    }));
  };

  const getSelectedVersion = (entry) => {
    if (entry.type !== FILTER_TYPES.MEDICAL_REPORT) return null;
    const selectedVersionId = selectedVersions[entry.report_group_id];
    return (
      entry.versions.find((v) => v.version_id === selectedVersionId) ||
      entry.versions[entry.versions.length - 1]
    );
  };

  const filteredEntries = useMemo(() => {
    const allowed = new Set([
      FILTER_TYPES.APPOINTMENT,
      FILTER_TYPES.MEDICAL_REPORT,
      FILTER_TYPES.NEXT_APPOINTMENT_NOTE,
      FILTER_TYPES.CLIENT_NOTE,
      FILTER_TYPES.RECEPTION_NOTE,
      ...(canSeeNoteForFloorManager ? [FILTER_TYPES.FLOOR_MANAGER_NOTE] : []),
      ...(canSeeAppointmentEvents ? [FILTER_TYPES.APPOINTMENT_EVENTS] : []),
      FILTER_TYPES.FILE_EVENT,
      FILTER_TYPES.HISTORY_RECORD,
      FILTER_TYPES.WEBHOOK,
    ]);

    const base = timelineEntries.filter((e) => allowed.has(e.type));
    if (!filters || filters.length === 0) return base;
    const want = new Set(filters);
    return base.filter((e) => want.has(e.type));
  }, [
    timelineEntries,
    filters,
    canSeeAppointmentEvents,
    canSeeNoteForFloorManager,
  ]);

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error || !timelineEntries.length)
    return <Typography variant="h6">{t("client.noActivityLogs")}</Typography>;

  return (
    <>
      <Box
        sx={{
          alignItems: "center",
          gap: 2,
          display: "flex",
          justifyContent: "flex-end",
          position: "sticky",
          top: "calc(var(--app-sticky-top) + 168px)",
          pt: 2,
        }}
      >
        <FilterMenu
          selected={filters}
          onChange={setFilters}
          canSeeAppointmentEvents={canSeeAppointmentEvents}
          canSeeNoteForFloorManager={canSeeNoteForFloorManager}
        />

        {canSetNoteForHistoryAxis && (
          <Button
            sx={{ display: "flex" }}
            onClick={() => setNoteForHistoryAxisDialogOpen(true)}
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faPen} />}
            disabled={saving}
          >
            {t("client.addNote")}
          </Button>
        )}
        {isGrantedAny(roles, permissions.operations.medicalReportRequest) && (
          <ExportMenu client_id={clientId} />
        )}
      </Box>

      {noteForHistoryAxisDialogOpen && (
        <Dialog
          open
          onClose={() => setNoteForHistoryAxisDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {t("client.noteForHistoryAxis")}
            <IconButton
              aria-label="close"
              onClick={() => setNoteForHistoryAxisDialogOpen(false)}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              label={t("client.writeNote")}
              fullWidth
              multiline
              minRows={4}
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              disabled={saving}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setNoteForHistoryAxisDialogOpen(false)}
              disabled={saving}
            >
              {t("form.cancel")}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveNote}
              disabled={!noteInput.trim() || saving}
            >
              {saving ? <CircularProgress size={20} /> : t("buttons.save")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ maxWidth: 1200, pt: 0 }}>
        {filteredEntries.length === 0 && (
          <Typography
            variant="body1"
            sx={{ p: 3, fontSize: "1.3rem", textAlign: "center" }}
          >
            {t(
              "client.noFilteredEntries",
              "Nebyly nalezeny žádné záznamy pro vybraný filtr.",
            )}
          </Typography>
        )}
        <Timeline>
          {filteredEntries.map((entry) => {
            const getIcon = (entry) => {
              if (entry.type === FILTER_TYPES.APPOINTMENT) return faKeyboard;
              if (entry.type === FILTER_TYPES.APPOINTMENT_EVENTS) {
                return getAppointmentEventIcon(entry.event_type);
              }
              if (entry.type === FILTER_TYPES.NEXT_APPOINTMENT_NOTE)
                return faNoteSticky;
              if (entry.type === FILTER_TYPES.RECEPTION_NOTE)
                return faCalendarCheck;
              if (entry.type === FILTER_TYPES.FLOOR_MANAGER_NOTE) return faPen;
              if (entry.type === FILTER_TYPES.CLIENT_NOTE) return faPen;
              if (entry.type === FILTER_TYPES.FILE_EVENT) return faFile;
              if (entry.type === FILTER_TYPES.MEDICAL_REPORT) {
                const selectedVersion = getSelectedVersion(entry);
                return selectedVersion?.is_original
                  ? faChalkboardTeacher
                  : faPen;
              }
              return faChalkboardTeacher;
            };

            return (
              <TimelineItem
                sx={{
                  display: "flex",
                  alignItems: "stretch",
                  minHeight: "120px",
                }}
                key={entry.id}
              >
                <TimelineOppositeContent
                  sx={{ textAlign: "left", flex: 0.3, pr: 4, pb: 5 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: "bold",
                      color: "primary.main",
                      fontSize: "1.6rem",
                    }}
                  >
                    <TimelineEntryTitle entry={entry} t={t} />
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      color: theme.palette.secondary.main,
                      mt: 1,
                    }}
                  >
                    {f.shortDateTime(getEntryDisplayTime(entry))}
                  </Typography>

                  <TimelineEntryOppositeContent
                    entry={entry}
                    selectedVersions={selectedVersions}
                  />
                  {canSeeEditedBy && (
                    <>
                      {entry.type === FILTER_TYPES.MEDICAL_REPORT &&
                        entry.versions.length > 1 && (
                          <Box sx={{ mt: 1 }}>
                            <FormControl
                              size="small"
                              sx={{
                                minWidth: 150,
                                maxWidth: 220,
                                "& .MuiOutlinedInput-root": {
                                  pl: 0,
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "none" },
                                  "&:hover fieldset": { border: "none" },
                                  "&.Mui-focused fieldset": { border: "none" },
                                },
                                "& .MuiInputLabel-root": { display: "none" },
                              }}
                            >
                              <Select
                                value={
                                  selectedVersions[entry.report_group_id] ||
                                  entry.versions[entry.versions.length - 1]
                                    .version_id
                                }
                                onChange={(e) =>
                                  handleVersionChange(
                                    entry.report_group_id,
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                sx={{
                                  backgroundColor: "transparent",
                                  "& .MuiSelect-select": {
                                    fontSize: "0.875rem",
                                    color: "text.secondary",
                                  },
                                }}
                                startAdornment={
                                  <FontAwesomeIcon
                                    icon={faHistory}
                                    style={{
                                      color: theme.palette.text.secondary,
                                      marginRight: "0.5rem",
                                    }}
                                  />
                                }
                              >
                                {entry.versions
                                  .slice()
                                  .reverse()
                                  .map((version) => (
                                    <MenuItem
                                      key={version.version_id}
                                      value={version.version_id}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: version.is_latest
                                            ? "bold"
                                            : "normal",
                                        }}
                                      >
                                        {version.is_original
                                          ? t("client.original", "Originál")
                                          : f.date(version.edited_at)}
                                      </Typography>
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}

                      {entry.type === FILTER_TYPES.APPOINTMENT &&
                        entry.versions?.length > 1 && (
                          <Box sx={{ mt: 1 }}>
                            <FormControl
                              size="small"
                              sx={{
                                minWidth: 150,
                                maxWidth: 220,
                                "& .MuiOutlinedInput-root": {
                                  pl: 0,
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "none" },
                                  "&:hover fieldset": { border: "none" },
                                  "&.Mui-focused fieldset": { border: "none" },
                                },
                                "& .MuiInputLabel-root": { display: "none" },
                              }}
                            >
                              <Select
                                value={
                                  selectedVersions[entry.group_id] ||
                                  entry.versions[entry.versions.length - 1]
                                    .version_id
                                }
                                onChange={(e) =>
                                  handleVersionChange(
                                    entry.group_id,
                                    e.target.value,
                                  )
                                }
                                displayEmpty
                                sx={{
                                  backgroundColor: "transparent",
                                  "& .MuiSelect-select": {
                                    fontSize: "0.875rem",
                                    color: "text.secondary",
                                  },
                                }}
                                startAdornment={
                                  <FontAwesomeIcon
                                    icon={faHistory}
                                    style={{
                                      color: theme.palette.text.secondary,
                                      marginRight: "0.5rem",
                                    }}
                                  />
                                }
                              >
                                {entry.versions
                                  .slice()
                                  .reverse()
                                  .map((v) => (
                                    <MenuItem
                                      key={v.version_id}
                                      value={v.version_id}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: v.is_latest
                                            ? "bold"
                                            : "normal",
                                        }}
                                      >
                                        {v.is_original
                                          ? t("client.original", "Originál")
                                          : f.date(v.edited_at)}
                                      </Typography>
                                    </MenuItem>
                                  ))}
                              </Select>
                            </FormControl>

                            {(() => {
                              const sel =
                                entry.versions.find(
                                  (v) =>
                                    v.version_id ===
                                    (selectedVersions[entry.group_id] ||
                                      entry.versions[entry.versions.length - 1]
                                        .version_id),
                                ) || entry.versions[entry.versions.length - 1];

                              return !sel.is_original ? (
                                <Box sx={{ color: "text.disabled", mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", fontSize: "1rem" }}
                                  >
                                    {t("client.editedBy")}:{" "}
                                    {sel.edited_by_name ||
                                      entry.employee_name ||
                                      t("client.unknownEmployee")}
                                  </Typography>
                                  {sel.edited_at && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "1rem",
                                        display: "block",
                                      }}
                                    >
                                      {f.shortDateTime(sel.edited_at)}
                                    </Typography>
                                  )}
                                </Box>
                              ) : null;
                            })()}
                          </Box>
                        )}

                      {/* RECEPTIONIST NOTE: version dropdown + edited by/date */}
                      {entry.type === FILTER_TYPES.RECEPTION_NOTE &&
                        entry.versions?.length > 1 && (
                          <Box sx={{ mt: 1 }}>
                            <FormControl
                              size="small"
                              sx={{
                                minWidth: 150,
                                maxWidth: 220,
                                "& .MuiOutlinedInput-root": {
                                  pl: 0,
                                  backgroundColor: "transparent",
                                  "& fieldset": { border: "none" },
                                  "&:hover fieldset": { border: "none" },
                                  "&.Mui-focused fieldset": { border: "none" },
                                },
                                "& .MuiInputLabel-root": { display: "none" },
                              }}
                            >
                              <Select
                                value={
                                  selectedVersions[entry.id] ||
                                  entry.versions[entry.versions.length - 1]
                                    .version_id
                                }
                                onChange={(e) =>
                                  setSelectedVersions((prev) => ({
                                    ...prev,
                                    [entry.id]: e.target.value,
                                  }))
                                }
                                displayEmpty
                                sx={{
                                  backgroundColor: "transparent",
                                  "& .MuiSelect-select": {
                                    fontSize: "0.875rem",
                                    color: "text.secondary",
                                  },
                                }}
                                startAdornment={
                                  <FontAwesomeIcon
                                    icon={faHistory}
                                    style={{
                                      color: theme.palette.text.secondary,
                                      marginRight: "0.5rem",
                                    }}
                                  />
                                }
                              >
                                {[...entry.versions].reverse().map((v) => (
                                  <MenuItem
                                    key={v.version_id}
                                    value={v.version_id}
                                  >
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: v.is_latest
                                          ? "bold"
                                          : "normal",
                                      }}
                                    >
                                      {v.is_original
                                        ? t("client.original", "Originál")
                                        : f.date(v.edited_at)}
                                    </Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {(() => {
                              const sel =
                                entry.versions.find(
                                  (v) =>
                                    v.version_id ===
                                    (selectedVersions[entry.id] ||
                                      entry.versions[entry.versions.length - 1]
                                        .version_id),
                                ) || entry.versions[entry.versions.length - 1];

                              return !sel.is_original ? (
                                <Box sx={{ color: "text.disabled", mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{ display: "block", fontSize: "1rem" }}
                                  >
                                    {t("client.editedBy")}:{" "}
                                    {sel.edited_by_name ||
                                      t("client.unknownEmployee")}
                                  </Typography>
                                  {sel.edited_at && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "1rem",
                                        display: "block",
                                      }}
                                    >
                                      {f.shortDateTime(sel.edited_at)}
                                    </Typography>
                                  )}
                                </Box>
                              ) : null;
                            })()}
                          </Box>
                        )}
                    </>
                  )}

                  {entry.type !== FILTER_TYPES.FILE_EVENT && (
                    <Box sx={{ mt: 1, pb: "1rem" }}>
                      <PersonAvatar
                        name={
                          entry.type === FILTER_TYPES.APPOINTMENT_EVENTS
                            ? entry.employee_name || entry.user_name
                            : entry.employee_name
                        }
                        label={entry.employee_role}
                      />
                    </Box>
                  )}
                </TimelineOppositeContent>

                <TimelineSeparator>
                  {/*<Tooltip*/}
                  {/*  title={getAppointmentEventTitle(*/}
                  {/*    entry.event_type,*/}
                  {/*    entry.service_name,*/}
                  {/*  )}*/}
                  {/*>*/}
                  <TimelineDot
                    sx={{
                      boxShadow: "none",
                      aspectRatio: "1",
                      padding: "8px",
                      color: "primary.main",
                      backgroundColor: "divider",
                      fontSize: "1.8rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FontAwesomeIcon icon={getIcon(entry)} />
                  </TimelineDot>
                  {/*</Tooltip>*/}
                  <TimelineConnector
                    sx={{ backgroundColor: "grey.300", width: "1px" }}
                  />
                  <TimelineDot
                    sx={{
                      padding: "1px",
                      marginTop: 0,
                      boxShadow: "none",
                      alignSelf: "center",
                      color: "grey.300",
                    }}
                  />
                </TimelineSeparator>

                <TimelineContent sx={{ pl: 3, paddingBottom: 5 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      px: 3,
                      py: 1.5,
                      width: "100%",
                      border: "1px solid",
                      borderColor: "divider",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                    }}
                  >
                    <TimelineEntryContent
                      entry={entry}
                      fetchTimeline={fetchTimeline}
                      selectedVersions={selectedVersions}
                      canEdit={canEdit}
                      clientId={clientId}
                    />
                    {entry.type === FILTER_TYPES.APPOINTMENT ? (
                      (() => {
                        const selId =
                          selectedVersions[entry.group_id] ||
                          (entry.versions.find((v) => v.is_latest)
                            ?.version_id ??
                            entry.versions[entry.versions.length - 1]
                              ?.version_id);

                        const selectedVersion =
                          entry.versions.find((v) => v.version_id === selId) ||
                          entry.versions[entry.versions.length - 1];

                        return (
                          <>
                            <AppointmentTimelineItem
                              key={selectedVersion.version_id}
                              entry={{
                                ...entry,
                                id: selectedVersion.version_id,
                                filled_data: selectedVersion.filled_data,
                                template: selectedVersion.template,
                                template_id: selectedVersion.template_id,
                                isEditable:
                                  selectedVersion.is_editable === true,
                              }}
                              canEdit={canEdit}
                              onSave={async (formData) => {
                                await createEditedFilledServiceTemplate({
                                  previous_id: selectedVersion.version_id,
                                  appointment_id: entry.appointment_id,
                                  template_id: selectedVersion.template_id,
                                  filled_data: formData,
                                });
                                displaySnackbarMessage({
                                  title: "Editace zápisu proběhla úspěšně.",
                                  content: "",
                                });
                                fetchTimeline();
                              }}
                            />

                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 8,
                                right: 8,
                                display: "flex",
                                gap: 1,
                              }}
                            ></Box>
                          </>
                        );
                      })()
                    ) : entry.type === FILTER_TYPES.APPOINTMENT_EVENTS ? (
                      <AppointmentEventContent entry={entry} />
                    ) : entry.type === FILTER_TYPES.NEXT_APPOINTMENT_NOTE ? (
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {entry.note}
                      </Typography>
                    ) : entry.type === FILTER_TYPES.CLIENT_NOTE ||
                      entry.type === FILTER_TYPES.FLOOR_MANAGER_NOTE ? (
                      <ReceptionNoteContent entry={entry} />
                    ) : entry.type === FILTER_TYPES.FILE_EVENT ? (
                      <Box>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                          <strong>{t("client.documentClass", "Typ")}:</strong>{" "}
                          {entry.collection_name}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                          <strong>{t("client.documentName", "Soubor")}:</strong>{" "}
                          {entry.file_name}
                        </Typography>
                        {entry.state_name && (
                          <Typography variant="body1">
                            <strong>
                              {t("client.documentState", "Stav")}:
                            </strong>{" "}
                            {entry.state_name}
                          </Typography>
                        )}
                      </Box>
                    ) : null}
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
        <Box sx={{ height: "100px" }} />
      </Box>
    </>
  );
};

export default ClientActivityLogTimeline;
